'use client'

import { type common, host } from '@smokeballdev/smokeball-client-sdk'
import { v4 } from 'uuid'
import { sleep } from '../utils'

type SbSdkGetter = {
  get: () => common.Api
}

// Helper function to create a fake JWT token with far expiration
const createFakeJwtToken = (): string => {
  // Create a JWT with header, payload, and fake signature
  const header = {
    alg: 'HS256',
    typ: 'JWT'
  }
  
  const payload = {
    sub: 'fake-user-id',
    iat: Math.floor(Date.now() / 1000), // issued at (current time)
    exp: Math.floor(Date.now() / 1000) + (365 * 24 * 60 * 60), // expires in 1 year
    aud: 'fake-audience',
    iss: 'fake-issuer'
  }
  
  // Base64 encode the header and payload
  const encodedHeader = btoa(JSON.stringify(header))
  const encodedPayload = btoa(JSON.stringify(payload))
  const fakeSignature = 'fake-signature-' + Math.random().toString(36).substr(2, 9)
  
  return `${encodedHeader}.${encodedPayload}.${fakeSignature}`
}

export const createAdminDevSdkWrapper = (): SbSdkGetter => {
  let context: any = {}
  // Cache for matter details to avoid duplicate API calls
  const matterCache = new Map<string, any>()
  // Cache for contact details to avoid duplicate API calls
  const contactCache = new Map<string, any>()
  // Cache for staff data to avoid duplicate API calls
  const staffCache = new Map<string, { data: any[], timestamp: number }>()
  // Cache duration for staff data (5 minutes)
  const STAFF_CACHE_DURATION = 5 * 60 * 1000
  // Track if we have a valid session
  let hasValidSession = false
  // Cache the fake JWT token for consistency
  let cachedFakeToken: string | null = null


  const checkSession = async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/session')
      if (response.ok) {
        const sessionData = await response.json()
        const isValid = !!(sessionData?.user?.id)
        return isValid
      }
      return false
    } catch (error) {
      console.error('[ADMIN-DEV-SDK] Session check error:', error)
      return false
    }
  }

  const fetchMatterDetails = async (matterId: string) => {
    // Check session before fetching matter details
    if (!hasValidSession) {
      return null
    }

    if (matterCache.has(matterId)) {
      return matterCache.get(matterId)
    }

    try {
      const response = await fetch(`/api/admin/sdk/matter-details?matterId=${matterId}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch matter details: ${response.status} ${response.statusText}`)
      }
      const matterData = await response.json()
      
      // Cache the full response
      matterCache.set(matterId, matterData)
      return matterData
    } catch (error) {
      console.error('[ADMIN-DEV-SDK] Error fetching matter details:', error)
      throw error
    }
  }

  const fetchContactDetails = async (contactId: string) => {
    // Check session before fetching contact details
    if (!hasValidSession) {
      return null
    }

    if (contactCache.has(contactId)) {
      return contactCache.get(contactId)
    }

    try {
      const response = await fetch(`/api/admin/sdk/contact?contactId=${contactId}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch contact details: ${response.status} ${response.statusText}`)
      }
      const contactData = await response.json()
      
      // Cache the full response
      contactCache.set(contactId, contactData)
      return contactData
    } catch (error) {
      console.error('[ADMIN-DEV-SDK] Error fetching contact details:', error)
      throw error
    }
  }

  return {
    get: (): common.Api => {
      return {
        context,
        init: async request => {
          // simulate awaiting sb sdk initialization
          await sleep(2000)
          
          // Check for valid NextAuth session first
          hasValidSession = await checkSession()
          
          if (!hasValidSession) {
            context = {}
            cachedFakeToken = null // Clear cached token when session is invalid
            staffCache.clear() // Clear staff cache when session is invalid
            return
          }
          
          // Get matterId from URL query string if available
          let matterIdFromUrl: string | undefined;
          if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            matterIdFromUrl = urlParams.get('matterId') || undefined;
          }
          
          // Fetch session data from API to get real account ID
          try {
            const response = await fetch('/api/auth/session')
            if (response.ok) {
              const sessionData = await response.json()
              
              context.firmId = sessionData?.user?.firmId
              context.userId = sessionData?.user?.id
              context.matterId = matterIdFromUrl // Use matter ID from URL
              context.sessionId = v4()
            } else {
              hasValidSession = false
              context = {}
              cachedFakeToken = null // Clear cached token when session is invalid
              staffCache.clear() // Clear staff cache when session is invalid
            }
          } catch (error) {
            console.error('[ADMIN-DEV-SDK] Error fetching session data:', error)
            hasValidSession = false
            context = {}
            cachedFakeToken = null // Clear cached token when session is invalid
            staffCache.clear() // Clear staff cache when session is invalid
          }
        },
        auth: {
          token: async () => {
            if (!hasValidSession) {
              return ''
            }
            // Create and cache the fake JWT token if not already cached
            if (!cachedFakeToken) {
              cachedFakeToken = createFakeJwtToken()
            }
            return cachedFakeToken
          },
          idToken: async () => {
            if (!hasValidSession) {
              return ''
            }
            // Create and cache the fake JWT token if not already cached
            if (!cachedFakeToken) {
              cachedFakeToken = createFakeJwtToken()
            }
            return cachedFakeToken
          }
        },
        communicate: {
          createMessage: props => {
            console.warn('[ADMIN-DEV-SDK] communicate.createMessage called (admin dev implementation)')
          },
          sendMessage: () => {
            console.warn('[ADMIN-DEV-SDK] communicate.sendMessage called (admin dev implementation)')
          }
        },
        memos: {
          openNew: () => {
            console.warn('[ADMIN-DEV-SDK] memos.openNew called (admin dev implementation)')
          }
        },
        contacts: {
          create: () => {
            throw new Error('contacts.create not implemented by admin dev SDK')
          },
          open: () => {
            throw new Error('contacts.open not implemented by admin dev SDK')
          },
          get: async (contactId: string) => {
            if (!hasValidSession) {
              throw new Error('No valid session')
            }
            
            if (!contactId) {
              throw new Error('[ADMIN-DEV-SDK] contacts.get: contactId is required')
            }
            
            // Skip MyFirm contact ID as it's a special identifier for the user's firm
            if (contactId === 'MyFirm') {
              return null
            }
            
            const contactData = await fetchContactDetails(contactId)
            
            if (!contactData) {
              throw new Error('Contact not found')
            }
            
            return contactData
          },
          observe: () => {
            throw new Error('contacts.observe not implemented by admin dev SDK')
          },
          update: () => {
            throw new Error('contacts.update not implemented by admin dev SDK')
          }
        },
        // implement below properly when required in admin dev mode
        correspondence: {} as any,
        files: {
          select: async (options?: {
            matterId?: string;
            confirmationText?: string;
            fileSelectionLimit?: number;
            showEmails?: boolean;
            title?: string;
            heading?: string;
            allowCombiningAsPdf?: boolean;
            allowConversionToPdf?: boolean;
          }) => {
            if (typeof window === 'undefined') {
              return []
            }

            if (!hasValidSession) {
              return []
            }

            try {
              // Dynamic import to avoid SSR issues
              const { AdminWebSelectFilesNiceModal } = await import('@/components/admin-web-select-files-nice-modal')
              const NiceModal = (await import('@ebay/nice-modal-react')).default
              
              // Use matterId from options first, then fall back to context
              const matterId = options?.matterId || context.matterId
              
              if (!matterId) {
                console.warn('[ADMIN-DEV-SDK] No matterId available for file selection')
                return []
              }
              
              // accountId is not needed for API calls (endpoints get it from auth token)
              // but pass it for consistency with the modal interface
              const accountId = context.firmId || ''
              
              // Show the modal and await the result
              const selectedFiles = await NiceModal.show(AdminWebSelectFilesNiceModal, {
                accountId,
                matterId
              })
              
              return selectedFiles || []
            } catch (error) {
              console.error('[ADMIN-DEV-SDK] Error showing file selection modal:', error)
              return []
            }
          }
        } as any,
        firm: {} as any,
        forms: {} as any,
        host: {
          version: async () => ({
            major: 0,
            minor: 0,
            patch: 0,
            build: 1,
            revision: 1
          }),
          type: () => host.HostType.Unknown,
          selectTab: () => { console.warn('[ADMIN-DEV-SDK] host.selectTab called (admin dev implementation)') },
          setCommands: () => { console.warn('[ADMIN-DEV-SDK] host.setCommands called (admin dev implementation)') },
          refresh: () => { console.warn('[ADMIN-DEV-SDK] host.refresh called (admin dev implementation)') },
          open: (urlParam?: string | { url?: string } | any) => { 
            if (typeof window !== 'undefined') {
              let urlToOpen = ''
              
              // Handle different parameter formats
              if (typeof urlParam === 'string') {
                urlToOpen = urlParam
              } else if (urlParam && typeof urlParam === 'object') {
                // Try common properties that might contain the URL
                urlToOpen = urlParam.url || urlParam.href || urlParam.link || ''
                
                // Log for debugging if we can't find a URL
                if (!urlToOpen) {
                  console.warn('[ADMIN-DEV-SDK] host.open: Could not extract URL from object:', urlParam)
                }
              }
              
              // Only open if we have a valid URL
              if (urlToOpen) {
                window.open(urlToOpen, '_blank', 'noopener,noreferrer')
              } else {
                console.warn('[ADMIN-DEV-SDK] host.open: No valid URL provided, param was:', urlParam)
              }
            } else {
              console.warn('[ADMIN-DEV-SDK] host.open called but window is not available')
            }
          },
          close: () => { console.warn('[ADMIN-DEV-SDK] host.close called (admin dev implementation)') }
        } as any,
        layouts: {} as any,
        matterItems: {
          get: async (matterId: string) => {
            if (!hasValidSession) {
              return { id: matterId || 'unknown', items: [] }
            }
            
            if (!matterId) {
              throw new Error('[ADMIN-DEV-SDK] matterItems.get: matterId is required')
            }
            
            const matterData = await fetchMatterDetails(matterId)
            if (!matterData || !matterData.items) {
              return { id: matterId, items: [] }
            }
            
            // Convert the nested items structure to a flat array for matterItems.get
            const flatItems: any[] = []
            
            Object.keys(matterData.items).forEach(categoryKey => {
              const categoryItems = matterData.items[categoryKey]
              if (Array.isArray(categoryItems)) {
                categoryItems.forEach(item => {
                  // Add the main item
                  flatItems.push({
                    id: item.id,
                    name: item.name || categoryKey,
                    type: item.type,
                    subItems: []
                  })
                  
                  // Add subItems if they exist
                  if (item.subItems && typeof item.subItems === 'object') {
                    const mainItemIndex = flatItems.length - 1
                    Object.keys(item.subItems).forEach(subCategoryKey => {
                      const subCategoryItems = item.subItems[subCategoryKey]
                      if (Array.isArray(subCategoryItems)) {
                        subCategoryItems.forEach(subItem => {
                          flatItems[mainItemIndex].subItems.push({
                            id: subItem.id,
                            name: subItem.name || subCategoryKey,
                            type: subItem.type
                          })
                        })
                      }
                    })
                  }
                })
              }
            })
            
            const result = {
              id: matterId,
              items: flatItems
            }
            
            return result
          }
        },
        matters: {
          get: async (matterId: string) => {
            if (!hasValidSession) {
              throw new Error('No valid session')
            }
            
            if (!matterId) {
              throw new Error('[ADMIN-DEV-SDK] matters.get: matterId is required')
            }
            
            const matterData = await fetchMatterDetails(matterId)
            if (!matterData) {
              throw new Error('Matter not found')
            }
            
            // Extract client and other-side IDs from the matter items/relationships
            let clientIds: string[] = []
            let otherSideIds: string[] = []
            
            if (matterData.items) {
              Object.keys(matterData.items).forEach(categoryKey => {
                const categoryItems = matterData.items[categoryKey]
                if (Array.isArray(categoryItems)) {
                  categoryItems.forEach(item => {
                    if (item.type === 'role') {
                      // Check main role item
                      if (item.isClient && item.contact?.id) {
                        clientIds.push(item.contact.id)
                      }
                      if (item.isOtherSide && item.contact?.id) {
                        otherSideIds.push(item.contact.id)
                      }
                      
                      // Check subitems
                      if (item.subItems && typeof item.subItems === 'object') {
                        Object.keys(item.subItems).forEach(subCategoryKey => {
                          const subCategoryItems = item.subItems[subCategoryKey]
                          if (Array.isArray(subCategoryItems)) {
                            subCategoryItems.forEach(subItem => {
                              if (subItem.isClient && subItem.contact?.id) {
                                clientIds.push(subItem.contact.id)
                              }
                              if (subItem.isOtherSide && subItem.contact?.id) {
                                otherSideIds.push(subItem.contact.id)
                              }
                            })
                          }
                        })
                      }
                    }
                  })
                }
              })
            }
            
            // Remove duplicates and filter out empty values
            clientIds = [...new Set(clientIds)].filter(id => id)
            otherSideIds = [...new Set(otherSideIds)].filter(id => id)
            
            // If no clients found, try to use the legacy clientId field as fallback
            if (clientIds.length === 0 && matterData.clientId) {
              clientIds = [matterData.clientId]
            }

            // Return the matter data in the expected format with all required fields
            const result = {
              id: matterData.id,
              number: matterData.number,
              description: matterData.description,
              matterTypeId: matterData.matterType?.id,
              status: matterData.status || 'Open',
              attorneyResponsibleId: matterData.attorneyResponsibleId || '',
              personAssistingId: matterData.personAssistingId || '',
              originatingAttorneyId: matterData.originatingAttorneyId || '',
              dateOpened: matterData.dateOpened || new Date().toISOString(),
              dateClosed: matterData.dateClosed || null,
              clientId: matterData.clientId || '', // Keep for backward compatibility
              clientIds, // New array format
              otherSideIds, // New array format
              notes: matterData.notes || '',
              billingFrequency: matterData.billingFrequency || 'Monthly'
            }
            
            return result as any
          },
          select: async () => {
            if (typeof window === 'undefined') {
              throw new Error('Window not available')
            }

            if (!hasValidSession) {
              throw new Error('No valid session')
            }

            try {
              // Dynamic import to avoid SSR issues
              const { AdminWebSelectMatterNiceModal } = await import('@/components/admin-web-select-matter-nice-modal')
              const NiceModal = (await import('@ebay/nice-modal-react')).default
              
              // Get account ID from context
              const accountId = context.firmId
              
              if (!accountId) {
                throw new Error('No accountId available for matter selection')
              }
              
              // Show the modal and await the result
              const selectedMatterId = await NiceModal.show(AdminWebSelectMatterNiceModal, {
                accountId
              })
              
              return selectedMatterId
            } catch (error) {
              console.error('[ADMIN-DEV-SDK] Error showing matter selection modal:', error)
              throw error
            }
          }
        } as any,
        relationships: {
          get: async (roleId: string) => {
            if (!hasValidSession) {
              return { relationships: [] }
            }
            
            // Get matterId from context
            const matterId = context.matterId
            if (!matterId) {
              return { relationships: [] }
            }
            
            const matterData = await fetchMatterDetails(matterId)
            if (!matterData || !matterData.items) {
              return { relationships: [] }
            }
            
            // Find the role with the given roleId and return its subItems as relationships
            const relationships: any[] = []
            
            // Iterate through all item categories to find the role
            Object.keys(matterData.items).forEach(categoryKey => {
              const categoryItems = matterData.items[categoryKey]
              if (Array.isArray(categoryItems)) {
                categoryItems.forEach(item => {
                  // Check if this is the role we're looking for
                  if (item.type === 'role' && item.id === roleId) {
                    // Add all subItems as relationships
                    if (item.subItems && typeof item.subItems === 'object') {
                      Object.keys(item.subItems).forEach(subCategoryKey => {
                        const subCategoryItems = item.subItems[subCategoryKey]
                        if (Array.isArray(subCategoryItems)) {
                          subCategoryItems.forEach(subItem => {
                            relationships.push({
                              id: subItem.id,
                              name: subItem.name,
                              role: subItem.role,
                              contactId: subItem.contact?.id,
                              isClient: subItem.isClient,
                              isOtherSide: subItem.isOtherSide,
                              contact: subItem.contact,
                              type: subItem.type
                            })
                          })
                        }
                      })
                    }
                  }
                })
              }
            })
            
            return { relationships }
          }
        } as any,
        roles: {
          get: async () => {
            if (!hasValidSession) {
              return { roles: [] }
            }
            
            // Get matterId from context
            const matterId = context.matterId
            if (!matterId) {
              return { roles: [] }
            }
            
            const matterData = await fetchMatterDetails(matterId)
            if (!matterData || !matterData.items) {
              return { roles: [] }
            }
            
            // Extract only main role items (not subItems - those are relationships)
            const roles: any[] = []
            
            // Iterate through all item categories
            Object.keys(matterData.items).forEach(categoryKey => {
              const categoryItems = matterData.items[categoryKey]
              if (Array.isArray(categoryItems)) {
                categoryItems.forEach(item => {
                  // Add only main role items (subItems will be handled by relationships.get)
                  if (item.type === 'role') {
                    roles.push({
                      id: item.id,
                      name: item.name,
                      role: item.role,
                      contactId: item.contact?.id,
                      isClient: item.isClient,
                      isOtherSide: item.isOtherSide,
                      contact: item.contact
                    })
                  }
                })
              }
            })
            
            return { roles }
          }
        } as any,
        staff: (() => {
          // Shared function to get all staff with caching
          const getAllStaff = async () => {
            if (!hasValidSession) {
              return []
            }

            const cacheKey = context.firmId || 'default'
            const cached = staffCache.get(cacheKey)
            const now = Date.now()
            
            // Return cached data if still valid
            if (cached && (now - cached.timestamp) < STAFF_CACHE_DURATION) {
              console.log('[ADMIN-DEV-SDK] Returning cached staff data')
              return cached.data
            }

            try {
              console.log('[ADMIN-DEV-SDK] Fetching staff data from API')
              const response = await fetch('/api/admin/sdk/staff')
              if (!response.ok) {
                throw new Error(`Failed to fetch staff: ${response.status} ${response.statusText}`)
              }
              const staffData = await response.json()
              const data = Array.isArray(staffData) ? staffData : []
              
              // Cache the result
              staffCache.set(cacheKey, { data, timestamp: now })
              
              return data
            } catch (error) {
              console.error('[ADMIN-DEV-SDK] Error fetching staff:', error)
              
              // Return cached data if available, even if stale, as fallback
              if (cached) {
                console.log('[ADMIN-DEV-SDK] Returning stale cached staff data as fallback')
                return cached.data
              }
              
              return []
            }
          }

          return {
            all: getAllStaff,
            get: async (staffId: string) => {
              if (!hasValidSession) {
                return null
              }

              if (!staffId) {
                throw new Error('[ADMIN-DEV-SDK] staff.get: staffId is required')
              }

              try {
                // Use the shared getAllStaff function
                const allStaff = await getAllStaff()
                const staff = allStaff.find((s: any) => s.id === staffId)
                return staff || null
              } catch (error) {
                console.error('[ADMIN-DEV-SDK] Error fetching staff member:', error)
                throw error
              }
            },
            observe: (callback: (staff: any[]) => void) => {
              if (!hasValidSession) {
                return
              }

              // Store the callback for later use
              const observerKey = `staff-observer-${Math.random().toString(36).substr(2, 9)}`
              
              // Initial fetch and callback using cached data
              const fetchAndNotify = async () => {
                try {
                  // Use the shared getAllStaff function
                  const staffArray = await getAllStaff()
                  callback(staffArray)
                } catch (error) {
                  console.error('[ADMIN-DEV-SDK] Error in staff observer:', error)
                  callback([])
                }
              }

              // Call immediately with current data
              fetchAndNotify()

              // For now, just return a cleanup function
              // In a real implementation, this would set up real-time updates
              return () => {
                console.log('[ADMIN-DEV-SDK] Staff observer cleanup called')
              }
            }
          }
        })() as any,
        subTasks: {} as any,
        tasks: {} as any,
        matterTypes: {
          getMany: async (options?: { ids?: string[] }) => {
            if (!hasValidSession) {
              return { matterTypes: [] }
            }

            // Get account ID from context
            const accountId = context.firmId
            if (!accountId) {
              console.warn('[ADMIN-DEV-SDK] No accountId available for matter types')
              return { matterTypes: [] }
            }

            try {
              const response = await fetch(`/api/admin/sdk/matter-types?accountId=${accountId}`)
              if (!response.ok) {
                throw new Error(`Failed to fetch matter types: ${response.status} ${response.statusText}`)
              }
              const matterTypes = await response.json()
              
              // Return in the expected format with matterTypes property
              return {
                matterTypes: Array.isArray(matterTypes) ? matterTypes : []
              }
            } catch (error) {
              console.error('[ADMIN-DEV-SDK] Error fetching matter types:', error)
              return { matterTypes: [] }
            }
          }
        } as any,
        expenses: {} as any,
      }
    }
  }
} 